import { VOOrchestra, VOOrchestraTrack } from "../model/DataStructure.js";
import { Labels } from "../model/Labels.js";
import { TopoSort } from "../model/TopoSort.js";
import { Connections } from "./Connections.js";
import { Decoration } from "./Decoration.js";
import { Mixer, MixerFaderState } from "./Mixer.js";
import { OrchestraInstrument } from "./OrchestraInstrument.js";
import { Sound } from "../audio/Sound.js";
import { Soundwave, SoundwaveChangeEvent } from "./Soundwave.js";
import { ProgressList } from "../model/ProgressList.js";

export interface OrchestraOptions {
    container: HTMLDivElement;
    data: VOOrchestra;
    mixer: Mixer;
    showConnections?: boolean;
    showTotalSoundwave?: boolean;
    totalSoundwaveDynamicVolume?: boolean;
    labels: Labels;
    instrumentShadow?: boolean;
    instrumentGradient?: boolean;
}

interface BufferInfo {
    instrument: VOOrchestraTrack;
    sound: Sound;
}

export class Orchestra {

    public readonly onSeek = e5.Signal.create<[SoundwaveChangeEvent]>();
    public readonly onChangeFaders = e5.Signal.create<[MixerFaderState[]]>();
    public readonly onChangeTime = e5.Signal.create<[number]>();

    public readonly element: HTMLDivElement;
    public readonly data: VOOrchestra;

    private _options: OrchestraOptions;
    private _created: boolean = false;
    private _items: OrchestraInstrument[] = [];
    private _audioContext: AudioContext;
    private _when: number = 0;
    private _offset: number = 0;
    private _totalDuration: number = 0;
    private _itemsLookup: { [index: string]: OrchestraInstrument } = {};
    private _time = 0;
    private _decoration: Decoration;
    private _gain: GainNode;
    private _playing: boolean = false;

    constructor(options: OrchestraOptions) {

        options = e5.object.merge({
            container: null,
            showConnections: false,
            showTotalSoundwave: true,
            totalSoundwaveDynamicVolume: false,
            instrumentShadow: false,
            instrumentGradient: false
        } as OrchestraOptions, options);
        this._options = options;

        this.data = options.data;

        this.element = document.createElement("div");
        this.element.className = "orchestra e5-hidden";
        this.element.innerHTML = `
        <div class="orchestra__connections"></div>
        <div class="orchestra__decorations"></div>        
        <div class="orchestra__bar-wrapper">
            <div class="orchestra__bar orchestra__bar--hidden"></div>
        </div>
        <div class="orchestra__instrument-list"></div>
        <div class="orchestra__control-bottom">
            <div class="orchestra__control-bottom-label">
                ${options.labels.html("orchestra.orchestra.mix_it")}
            </div>
            <div class="orchestra__soundwave e5-hidden"></div>
        </div>        
        `;

        if (options.container) {
            options.container.appendChild(this.element);
        }
    }

    public async create(loadProgress: ProgressList, processingProgress: ProgressList) {
        if (this._created) {
            return true;
        }
        this._created = true;

        try {
            this._audioContext = new AudioContext();
        } catch (error) {

        }
        await this._audioContext.suspend();

        this._gain = this._audioContext.createGain();
        this._gain.gain.value = 0;
        this._gain.connect(this._audioContext.destination);

        const infos: BufferInfo[] = [];
        for (let instrument of this._options.data.instruments) {
            infos.push({
                instrument: instrument,
                sound: new Sound({
                    context: this._audioContext
                })
            })
        }

        await Promise.all(
            infos.map(
                (info) => {
                    return info.sound.load(info.instrument.track.url,
                        info.instrument.instrument.title + " > Track > " + info.instrument.track.filename,
                        loadProgress.add(info.instrument.track.filesize),
                        processingProgress.add(1));
                }
            )
        );

        for (let info of infos) {
            this._totalDuration = Math.max(this._totalDuration, info.sound.buffer.duration);
        }


        this._decoration = new Decoration({
            container: this.element.querySelector(".orchestra__decorations"),
            names: [
                "orchestra-1",
                "orchestra-3",
                "orchestra-4",
                "orchestra-5",
                "orchestra-6",
                "orchestra-7",
                "orchestra-14"
            ]
        });


        // const posSorted = infos.slice().sort((a, b) => {
        //     if (a.instrument.instrument.y == b.instrument.instrument.y) {
        //         return a.instrument.instrument.x - b.instrument.instrument.x;
        //     }
        //     return a.instrument.instrument.y - b.instrument.instrument.y;
        // });

        // const posSorted = infos.slice().sort((a, b) => {
        //     if (a.instrument.instrument.y == b.instrument.instrument.y) {
        //         return a.instrument.instrument.x - b.instrument.instrument.x;
        //     }
        //     return a.instrument.instrument.y - b.instrument.instrument.y;
        // });

        let tips = infos.slice().map((a) => {
            return {
                info: a,
                x: a.instrument.place.x,
                y: a.instrument.place.y
            }
        });
        tips = TopoSort.sort(tips, 200) as any;

        const list = this.element.querySelector<HTMLElement>(".orchestra__instrument-list");
        const originX = 1920 * 0.5;
        const originY = 1080;// * 0.5;
        for (let k of tips) {

            const info = k.info;
            const instrument = info.instrument;
            const dx = originX - (instrument.place.x + 200);
            const dy = originY - instrument.place.y;
            const angle = Math.atan2(dy, dx) * (180 / Math.PI);

            const item = new OrchestraInstrument({
                data: info.instrument,
                sound: info.sound,
                shadowAngle: angle,
                totalDuration: this._totalDuration,
                audioContext: this._audioContext,
                destination: this._gain,
                volume: this._options.mixer.getVolume(info.instrument.index),
                container: list,
                shadow: this._options.instrumentShadow,
                backgroundGradient: this._options.instrumentGradient
            });

            item.onSeek.add((e) => this.onSeek.dispatch(e));

            this._items.push(item);
            this._itemsLookup[item.data.index] = item;
        }

        const bar = this.element.querySelector(".orchestra__bar");
        for (let faderIndex = 0; faderIndex < 16; ++faderIndex) {
            let thumb = document.createElement("div");
            thumb.className = "orchestra-instrument__thumb";

            let item = this._items.find((item) => item.data.index === faderIndex);
            if (item) {
                thumb.classList.add("orchestra-instrument__thumb--used");
                thumb.innerHTML = `<img src="${item.data.instrument.thumbnail.url}" />`;
            }

            bar.appendChild(thumb);
        }

        this.onSeek.add((info) => {
            this.setTime(info.time);
        });

        if (this._options.showTotalSoundwave) {

            const soundwaveContainer = this.element.querySelector<HTMLElement>(".orchestra__soundwave");
            soundwaveContainer.classList.remove("e5-hidden");

            const soundwave = new Soundwave({
                container: soundwaveContainer,
                colorLeft: "rgba(0, 0, 0, 1)",
                colorRight: "rgba(0, 0, 0, 0.2)",
                colorBaseLineLeft: "rgba(255, 255, 255, 0.5)",
                colorBaseLineRight: "rgba(0, 0, 0, 0.2)",
            });
            soundwave.onSeek.add((e) => this.onSeek.dispatch(e));

            this.onChangeTime.add((time) => {
                soundwave.time = this._time;
            });

            if (this._options.totalSoundwaveDynamicVolume) {
                const updateTotalSoundwave = () => {
                    soundwave.infos = this._items.map(
                        (v) => {
                            return {
                                sound: v.sound,
                                volume: v.getVolume()
                            };
                        });
                };
                this.onChangeFaders.throttle(updateTotalSoundwave, 50);
                updateTotalSoundwave();
            } else {
                soundwave.infos = this._items.map(
                    (v) => {
                        return {
                            sound: v.sound,
                            volume: 1
                        };
                    });
            }
        }

        if (this._options.showConnections) {
            const root = window.getComputedStyle(document.documentElement);
            const thumbWidth = parseInt(root.getPropertyValue("--fader__width"));
            new Connections({
                edges: this._options.data.instruments.map((orchestraInstrument) => {

                    var instrument = orchestraInstrument.instrument;
                    return {
                        color: instrument.color,
                        target: {
                            x: orchestraInstrument.place.x + 120,
                            y: orchestraInstrument.place.y + 40,
                        },
                        source: {
                            x: (orchestraInstrument.index * thumbWidth + thumbWidth / 2) + (1920 - (16 * thumbWidth)) / 2,
                            y: 1080 - 80,
                        }
                    }
                }),
                container: this.element.querySelector(".orchestra__connections")
            });
        }

        e5.Player.onTick.add(() => this.tick());

        this._options.mixer.onChange.throttle(
            (states) => {
                this.setFaderStates(states);
            }, 100);

        return true;
    }

    private tick(): void {
        // if (this._audioContext.state !== "running") {
        //     return;
        // }

        if (!this._playing) {
            return;
        }



        const t = this._audioContext.currentTime - (this._when - this._offset);
        if (t === this._time) {
            return;
        }
        this._time = t;

        // console.log(this._audioContext.state);

        //Repeat
        if (this._time >= this._totalDuration) {
            this.setTime(0);
            return;
        }

        for (let item of this._items) {
            item.updateTime(this._time);
        }

        this.onChangeTime.dispatch(this._time);
    }

    private toggle(): void {
        if (this._audioContext.state === "suspended") {
            this.play();
        } else {
            this.pause();
        }
    }

    private get autoVolume(): number {
        return this._gain.gain.value;
    }

    private set autoVolume(value: number) {
        value = e5.Calc.clamp(value, 0, 1);
        if (this._gain.gain.value === value) {
            return;
        }
        this._gain.gain.value = value;
        if (value === 0) {
            // if (this._audioContext.state === "running") {
            this.pause();
            // }
        } else {
            // if (this._audioContext.state === "suspended") {
            this.play();
            // }
        }
    }

    private pause(): void {
        if (!this._playing) {
            return;
        }
        this._playing = false;
        this._audioContext.suspend();
    }

    private play(): void {
        if (this._playing) {
            return;
        }
        this._playing = true;
        this._audioContext.resume();

        this._when = this._audioContext.currentTime + 0.1;
        this._offset = this._time;
        for (let t of this._items) {
            t.play(this._when, this._time);
        }
    }

    private setTime(time: number = 0): void {
        this._time = e5.Calc.clamp(time, 0, this._totalDuration);
        this._when = this._audioContext.currentTime + 0.1;
        this._offset = this._time;

        if (this._playing) {
            for (let t of this._items) {
                t.play(this._when, this._time);
            }
        } else {
            for (let t of this._items) {
                t.updateTime(this._time);
            }
            this.onChangeTime.dispatch(this._time);
        }
    }

    public setFaderStates(states: MixerFaderState[]): void {

        for (let state of states) {
            this._itemsLookup[state.index]?.setVolume(state.volume);
        }

        this.onChangeFaders.dispatch(states);
    }



    public restart(): void {
        this.setTime();
    }

    public async show() {

        if (!this._created) {
            throw new Error("Call Orchestra.create() before invoking Orchestra.show().");
        }

        // await this.create();

        this.pause();
        this.setTime(0);

        this.element.classList.remove("e5-hidden");
        this.element.offsetWidth;

        this.element.classList.toggle("orchestra--hidden", false);
        this._decoration.visible = true;
        this.element.querySelector(".orchestra__bar")
            .classList.toggle("orchestra__bar--hidden", false);
        for (let item of this._items) {
            item.visible = true;
        }

        await new Promise((resolve) => {
            setTimeout(resolve, 700);
        });

        gsap.to(this, {
            duration: 0.5,
            autoVolume: 1
        });
    }

    public async hide() {
        //this.pause();

        this._decoration.visible = false;
        this.element.classList.toggle("orchestra--hidden", true);
        this.element.querySelector(".orchestra__bar")
            .classList.toggle("orchestra__bar--hidden", true);
        for (let item of this._items) {
            item.visible = false;
        }

        gsap.to(this, { duration: 0.5, autoVolume: 0 });

        return new Promise<any>((resolve, reject) => {
            setTimeout(() => {
                this.element.classList.add("e5-hidden");
                resolve(null);
            }, 500);
        });
    }
}